import { isPreviewDeployment, isDevelopment, isStaging } from "@tellescope/utils";
import { STAGING_API_URL, TEST_API_URL } from "./api";
import { getSelectedHost } from "@tellescope/ui";

// Legacy portal URL for local development (when using local API)
export const DEV_PORTAL_URL = "http://localhost:3030";

// Hard-coded subdomain used in iframe embed URLs instead of the real org subdomain
const EMBEDDED_SUBDOMAIN = "___embedded___";

/**
 * Extracts the subdomain or custom portal URL from the current hostname.
 * Used for fetching organization info via /organization-info endpoint.
 *
 * URL patterns:
 * - Production: {subdomain}.portal.tellescope.com
 * - Staging: {subdomain}.staging-portal.tellescope.com
 * - Preview: Uses hardcoded "new" subdomain for staging org
 * - Custom domain: Uses customPortalURL (the full origin)
 * - Dev (localhost): Uses customPortalURL with localhost origin
 */
export const getPortalIdentifier = (): { subdomain?: string; customPortalURL?: string } => {
  const hostname = window.location.hostname;

  // Preview deployments (S3/CloudFront PR previews) use a configurable subdomain
  if (isPreviewDeployment()) {
    return { subdomain: import.meta.env.VITE_PREVIEW_SUBDOMAIN ?? "new" };
  }

  // Tellescope-hosted portals (subdomain.portal.tellescope.com or subdomain.staging-portal.tellescope.com)
  if (hostname.includes("tellescope.com")) {
    return { subdomain: hostname.split(".")[0] };
  }

  // Custom portal URL (non-tellescope.com hostname)
  return { customPortalURL: window.location.origin };
};

/**
 * Returns the base URL for the legacy portal based on organization data.
 * Always uses the subdomain-based URL (not customPortalURL) to ensure consistent
 * behavior for embedded iframes and avoid cross-origin issues.
 *
 * In development, this matches the API environment being used (staging API = staging portal).
 */
export const getLegacyPortalURL = (): string => {
  // In development, determine portal URL based on the selected API host
  if (isDevelopment()) {
    const selectedHost = getSelectedHost();

    // Local API = local portal
    if (selectedHost.url === TEST_API_URL) {
      return DEV_PORTAL_URL;
    }

    // Staging API = staging portal
    if (selectedHost.url === STAGING_API_URL) {
      return `https://${EMBEDDED_SUBDOMAIN}.staging-portal.tellescope.com`;
    }

    // Production API = production portal
    return `https://${EMBEDDED_SUBDOMAIN}.portal.tellescope.com`;
  }

  // Staging environment (deployed staging app)
  if (isStaging()) {
    return `https://${EMBEDDED_SUBDOMAIN}.staging-portal.tellescope.com`;
  }

  // Production environment
  return `https://${EMBEDDED_SUBDOMAIN}.portal.tellescope.com`;
};

/**
 * Builds the URL for embedding a form completion in an iframe.
 * This uses the legacy portal's /link/form route.
 * Always uses the subdomain-based URL (not customPortalURL) for consistent embedding.
 */
export const buildFormCompletionURL = (params: {
  organization: { subdomain: string };
  formId: string;
  accessCode: string;
  authToken: string;
  automationStepId?: string;
  hideBg?: boolean;
  startAtNext?: boolean;
}): string => {
  const baseURL = getLegacyPortalURL();
  const searchParams = new URLSearchParams();

  searchParams.set("overrideSubdomain", params.organization.subdomain);
  searchParams.set("f", params.formId);
  searchParams.set("a", params.accessCode);
  searchParams.set("token", params.authToken);

  if (params.automationStepId) {
    searchParams.set("stepId", params.automationStepId);
  }

  if (params.hideBg) {
    searchParams.set("hideBg", "true");
  }

  if (params.startAtNext) {
    searchParams.set("startAtNext", "true");
  }

  return `${baseURL}/link/form?${searchParams.toString()}`;
};

/**
 * Builds the URL for embedding an appointment booking page in an iframe.
 * This uses the legacy portal's /book-an-appointment route.
 * Always uses the subdomain-based URL (not customPortalURL) for consistent embedding.
 */
export const buildAppointmentBookingURL = (params: {
  organization: { subdomain: string };
  bookingPageId: string;
  authToken: string;
  userIds?: string[];
  scheduledBy?: string;
}): string => {
  const baseURL = getLegacyPortalURL();
  const searchParams = new URLSearchParams();

  searchParams.set("overrideSubdomain", params.organization.subdomain);
  searchParams.set("token", params.authToken);
  searchParams.set("embedded", "true");

  if (params.userIds?.length) {
    searchParams.set("userIds", params.userIds.join(","));
  }

  if (params.scheduledBy) {
    searchParams.set("scheduledBy", params.scheduledBy);
  }

  return `${baseURL}/book-an-appointment/${params.bookingPageId}?${searchParams.toString()}`;
};
